/* @flow */
/* eslint-disable react/no-children-prop */

import classNames from 'classnames';
import * as React from 'react';
import { Link, useMatch, useLocation } from 'react-router-dom';
import { hasURLProtocol } from '@braindate/util/lib/url';

import styles from './MainNavLink.style';

import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import type { ReactElement } from 'src/shared/core/type/reactTypes';

type Props = {
  title?: string,
  to?: string,
  exact?: boolean,
  match?: string[],
  icon?: React.Node,
  children?: React.Node,
  preloadComp?: ReactElement,
  target?: string,
  rel?: string,
  className?: string,
  active?: boolean,
  disableActiveBackgroundColor?: boolean,
};

const useStyles = createUseThemeStyles(styles);

const MainNavLink = ({
  title,
  to,
  match,
  icon,
  children,
  preloadComp,
  target,
  rel,
  className,
  active,
  disableActiveBackgroundColor,
}: Props): React.Node => {
  const isExternalLink = to && hasURLProtocol(to);

  const routerMatch = useMatch(to || '/');
  const location = useLocation();

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onMouseOver = () => {
    if (preloadComp) {
      // $FlowIssue
      preloadComp.preload();
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */
  const classes = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const isActive =
    (to && ((match && match.includes(location.pathname)) || !!routerMatch)) ||
    !!active;

  const rootClasses = classNames(
    classes.root,
    {
      [`${classes['root-isSelected']}`]: isActive,
    },
    className,
  );

  let props = {
    className: rootClasses,
  };

  if (isExternalLink) {
    props = {
      ...props,
      href: to,
      target,
      rel,
    };
  } else {
    props = {
      ...props,
      to,
      onMouseOver,
    };
  }

  const LinkWrapper = ({ children }) =>
    React.createElement(
      isExternalLink ? 'a' : Link,
      // $FlowIssue: `to` is passed if element is `Link`
      props,
      <React.Fragment>{children}</React.Fragment>,
    );

  const Wrapper = ({ children }) =>
    !to ? (
      <div className={classNames(classes.root, rootClasses)}>{children}</div>
    ) : (
      <LinkWrapper>{children}</LinkWrapper>
    );

  const iconClassNames = classNames(classes.icon, {
    [classes['icon-isSelected']]: isActive,
    [classes['icon-noActiveBackground']]: disableActiveBackgroundColor,
    [classes['icon-noBorder']]: !icon,
  });

  return (
    <Wrapper>
      <span className={iconClassNames}>
        <span className={classes.iconInner}>
          {icon}
          {children}
        </span>
      </span>
      {title && (
        <span
          className={classNames(classes.label, {
            [`${classes['label-isSelected']}`]: isActive,
          })}
        >
          {title}
        </span>
      )}
    </Wrapper>
  );
};

export default React.memo<Props>(MainNavLink);
